import React, { Component } from 'react'
import { Animated, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import NavigationBarItem from './NavigationBarItem'
import NavigationBarPicker from './NavigationBarPicker'
import MyPlatform from '../MyPlatform'
import Icon from '../Icon'
import { colors } from '../styles/global'
import store from '../../redux/store'
import { toggleNavPicker } from '../../redux/actions'
import Hotkeys from 'react-hot-keys'
import dom from 'react-dom'
import { NAVBAR_ITEM_MAP } from '../../utils/TabNavigationConstants'
import { resolveTabBadgeAmount } from './tabBadges'

const finalHeight = 56
export default class NavigationBar extends Component {
    _isMounted = false

    constructor(props) {
        super(props)

        this.updateState = this.updateState.bind(this)
        const storeState = store.getState()

        this.state = {
            activeSearchForm: storeState.activeSearchForm,
            selectedNavItem: storeState.selectedNavItem,
            showShortcuts: storeState.showShortcuts,
            smallScreenNavigation: storeState.smallScreenNavigation,
            blockBackgroundTabShortcut: storeState.blockBackgroundTabShortcut,
            showCheatSheet: storeState.showCheatSheet,
            expanded: storeState.expandedNavPicker,
            route: storeState.route,
            height: new Animated.Value(0),
            forceTabletMargins: false,
            lastNavWidth: 0,
            isOverflowing: false,
            canScrollLeft: false,
            canScrollRight: false,
            unsubscribe: store.subscribe(this.updateState),
        }

        this.scrollDesktop = React.createRef()
        this.desktopScrollView = React.createRef()
        this.scrollMetrics = { x: 0, viewport: 0, content: 0 }
        this._wheelNode = null
        this.itemRefs = []
        this.hotkeyParentRef = React.createRef()
    }

    componentDidMount() {
        this._isMounted = true
        this.mountHotKey()
        this.attachWheel()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            (this.state.activeSearchForm && !this.state.expanded) ||
            (prevState.activeSearchForm && !this.state.activeSearchForm && this.state.expanded)
        ) {
            this.expandPicker()
        }

        if (this.state.route !== prevState.route) {
            this.mountHotKey()
        }

        this.attachWheel()
    }

    componentWillUnmount() {
        this._isMounted = false
        this.state.unsubscribe()
        this.detachWheel()
    }

    mountHotKey = () => {
        if (this.hotkeyParentRef?.current) {
            const parentDom = dom.findDOMNode(this.hotkeyParentRef.current)
            dom.render(<Hotkeys keyName={'tab'} onKeyDown={this.onShortcutSwitch} filter={e => true} />, parentDom)
        }
    }

    onLayoutChange = async ({ nativeEvent }) => {
        if (this.scrollDesktop !== undefined) {
            const sum = await MyPlatform.getElementWidth(this.scrollDesktop.current)

            if (this.state.lastNavWidth === 0) {
                this.setState({ lastNavWidth: sum })
            } else {
                if (nativeEvent.layout.width < sum && sum < this.state.lastNavWidth && !this.state.forceTabletMargins) {
                    this.setState({ forceTabletMargins: true })
                } else if (nativeEvent.layout.width >= sum && this.state.forceTabletMargins) {
                    this.setState({ forceTabletMargins: false })
                }
            }
        }
    }

    getScrollNode = () => {
        try {
            const ref = this.desktopScrollView.current
            return ref && ref.getScrollableNode ? ref.getScrollableNode() : null
        } catch (e) {
            return null
        }
    }

    // On desktop web the horizontal tab ScrollView has no scrollbar and a mouse wheel
    // only produces deltaY, so there is no input that reaches it. Translate the wheel
    // into horizontal scrolling (trackpad horizontal swipes keep working as before).
    attachWheel = () => {
        if (Platform.OS !== 'web') return
        const node = this.getScrollNode()
        if (node && node !== this._wheelNode) {
            this.detachWheel()
            node.addEventListener('wheel', this.onWheel, { passive: false })
            this._wheelNode = node
        }
    }

    detachWheel = () => {
        if (this._wheelNode) {
            this._wheelNode.removeEventListener('wheel', this.onWheel)
            this._wheelNode = null
        }
    }

    onWheel = e => {
        const node = this._wheelNode
        if (!node) return
        // No horizontal overflow → leave the event alone so the page can scroll vertically.
        if (node.scrollWidth <= node.clientWidth + 1) return
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
        if (!delta) return
        node.scrollLeft += delta
        e.preventDefault()
    }

    onDesktopScroll = ({ nativeEvent }) => {
        this.scrollMetrics = {
            x: nativeEvent.contentOffset.x,
            viewport: nativeEvent.layoutMeasurement.width,
            content: nativeEvent.contentSize.width,
        }
        this.updateScrollMetrics()
    }

    onDesktopContentSizeChange = contentWidth => {
        this.scrollMetrics.content = contentWidth
        this.updateScrollMetrics()
    }

    onDesktopScrollLayout = ({ nativeEvent }) => {
        this.scrollMetrics.viewport = nativeEvent.layout.width
        this.updateScrollMetrics()
    }

    updateScrollMetrics = () => {
        const { x, viewport, content } = this.scrollMetrics
        const isOverflowing = content > viewport + 1
        const canScrollLeft = x > 1
        const canScrollRight = x < content - viewport - 1
        if (
            isOverflowing !== this.state.isOverflowing ||
            canScrollLeft !== this.state.canScrollLeft ||
            canScrollRight !== this.state.canScrollRight
        ) {
            this.setState({ isOverflowing, canScrollLeft, canScrollRight })
        }
    }

    scrollByAmount = direction => {
        const ref = this.desktopScrollView.current
        if (!ref) return
        const { x, viewport } = this.scrollMetrics
        const step = Math.max(120, (viewport || 200) * 0.7)
        const target = direction === 'left' ? x - step : x + step
        ref.scrollTo({ x: Math.max(0, target), animated: true })
    }

    renderScrollArrow = direction => {
        const enabled = direction === 'left' ? this.state.canScrollLeft : this.state.canScrollRight
        return (
            <TouchableOpacity
                style={[localStyles.scrollArrow, !enabled && localStyles.scrollArrowDisabled]}
                onPress={enabled ? () => this.scrollByAmount(direction) : undefined}
                disabled={!enabled}
            >
                <Icon
                    name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
                    size={24}
                    color={enabled ? colors.Text03 : colors.Grey400}
                />
            </TouchableOpacity>
        )
    }

    onShortcutSwitch = (s, e) => {
        let proceed = true
        if (e) {
            e.preventDefault()
            e.stopPropagation()

            let target = e.target || e.srcElement
            let tagName = target.tagName
            proceed =
                !(tagName.isContentEditable || tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA') &&
                target.className.indexOf('ql-editor') < 0
        }

        const { tabs } = this.props
        const { selectedNavItem, showCheatSheet, blockBackgroundTabShortcut } = this.state

        if (!showCheatSheet && !blockBackgroundTabShortcut && proceed) {
            const index = tabs.indexOf(selectedNavItem)
            if (index === tabs.length - 1) {
                this.itemRefs[0]?.onPress()
            } else {
                this.itemRefs[index + 1]?.onPress()
            }
        }
    }

    isNextShortcutTab = tab => {
        const { tabs } = this.props
        const { selectedNavItem, showCheatSheet, showShortcuts, blockBackgroundTabShortcut } = this.state

        if (!showCheatSheet && !blockBackgroundTabShortcut && showShortcuts) {
            const index = tabs.indexOf(selectedNavItem)
            if (index === tabs.length - 1) {
                return tabs[0] === tab
            } else {
                return tabs[index + 1] === tab
            }
        }
    }

    render() {
        const {
            tabs,
            isSecondary,
            showFollowedNotifications,
            feedAmount,
            showSearchBadges,
            invitationsAmount,
            integrationsAlertAmount,
            style,
        } = this.props

        const badgeAmountFor = tabItem => resolveTabBadgeAmount(tabItem, { invitationsAmount, integrationsAlertAmount })

        const { height, forceTabletMargins, expanded, selectedNavItem, smallScreenNavigation } = this.state
        if (!smallScreenNavigation || isSecondary) {
            const showScrollArrows = isSecondary && Platform.OS === 'web' && this.state.isOverflowing
            return (
                <View
                    onLayout={this.onLayoutChange}
                    style={[
                        localStyles.container,
                        smallScreenNavigation ? localStyles.containerUnderBreakpoint : undefined,
                        style,
                    ]}
                >
                    {showScrollArrows && this.renderScrollArrow('left')}
                    <View style={isSecondary && { flex: 1 }} ref={this.scrollDesktop}>
                        <ScrollView
                            ref={this.desktopScrollView}
                            style={{ flex: 1 }}
                            horizontal={true}
                            showsHorizontalScrollIndicator={false}
                            overScrollMode={'never'}
                            scrollEventThrottle={16}
                            onScroll={this.onDesktopScroll}
                            onContentSizeChange={this.onDesktopContentSizeChange}
                            onLayout={this.onDesktopScrollLayout}
                            contentContainerStyle={localStyles.itemsContainer}
                        >
                            {/* This is to insert dynamically the hotkey component */}
                            <View ref={this.hotkeyParentRef} />

                            {tabs.map((tabItem, i) => (
                                <View key={i}>
                                    <NavigationBarItem
                                        ref={ref => (this.itemRefs[i] = ref)}
                                        selected={selectedNavItem === tabItem}
                                        showSearchBadges={showSearchBadges}
                                        isSecondary={isSecondary}
                                        feedAmount={
                                            (NAVBAR_ITEM_MAP[tabItem] === 'Updates' || tabItem === 'Updates') &&
                                            feedAmount
                                        }
                                        badgeAmount={badgeAmountFor(tabItem)}
                                        showFollowedNotifications={showFollowedNotifications}
                                        forceTabletMargins={forceTabletMargins}
                                        isNextShortcutTab={this.isNextShortcutTab(tabItem)}
                                    >
                                        {tabItem}
                                    </NavigationBarItem>
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                    {showScrollArrows && this.renderScrollArrow('right')}
                </View>
            )
        } else {
            return (
                <View style={[localStyles.containerMobile, { ...style }]}>
                    <View style={localStyles.subContainerMobile}>
                        <View style={localStyles.subMobile}>
                            <NavigationBarPicker
                                expanded={expanded}
                                onPress={this.expandPicker}
                                feedAmount={!expanded && feedAmount}
                                showFollowedNotifications={showFollowedNotifications}
                            >
                                {selectedNavItem}
                            </NavigationBarPicker>
                        </View>
                    </View>

                    <Animated.View style={[localStyles.itemsContainerMobile, { height: height }]}>
                        <ScrollView horizontal={true} showsHorizontalScrollIndicator={false} overScrollMode={'never'}>
                            <View style={{ flexDirection: 'row' }}>
                                {tabs.map((tabItem, i) => (
                                    <NavigationBarItem
                                        key={i}
                                        isMobile
                                        selected={selectedNavItem === tabItem}
                                        showSearchBadges={showSearchBadges}
                                        expandPicker={this.expandPicker}
                                        feedAmount={
                                            (NAVBAR_ITEM_MAP[tabItem] === 'Updates' || tabItem === 'Updates') &&
                                            feedAmount
                                        }
                                        showFollowedNotifications={showFollowedNotifications}
                                    >
                                        {tabItem}
                                    </NavigationBarItem>
                                ))}
                            </View>
                        </ScrollView>
                    </Animated.View>
                </View>
            )
        }
    }

    expandPicker = () => {
        if (this.state.expanded) {
            Animated.timing(
                // Animate value over time
                this.state.height, // The value to drive
                {
                    toValue: 0, // Animate to final value
                    duration: 200,
                    // height is a layout prop, so the native driver cannot drive it.
                    useNativeDriver: false,
                }
            ).start(this.toggleNavPickerOff) // Start the animation
        } else {
            Animated.timing(
                // Animate value over time
                this.state.height, // The value to drive
                {
                    toValue: finalHeight, // Animate to final value
                    duration: 200,
                    // height is a layout prop, so the native driver cannot drive it.
                    useNativeDriver: false,
                }
            ).start(this.toggleNavPickerOn) // Start the animation
        }
    }

    updateState() {
        if (!this._isMounted) return

        const storeState = store.getState()
        this.setState({
            activeSearchForm: storeState.activeSearchForm,
            expanded: storeState.expandedNavPicker,
            showShortcuts: storeState.showShortcuts,
            selectedNavItem: storeState.selectedNavItem,
            smallScreenNavigation: storeState.smallScreenNavigation,
            blockBackgroundTabShortcut: storeState.blockBackgroundTabShortcut,
            showCheatSheet: storeState.showCheatSheet,
            route: storeState.route,
        })
    }

    toggleNavPickerOff = () => {
        store.dispatch(toggleNavPicker(false))
    }

    toggleNavPickerOn = () => {
        store.dispatch(toggleNavPicker(true))
    }
}

const localStyles = StyleSheet.create({
    container: {
        // backgroundColor: 'white',
        flexDirection: 'row',
        borderBottomColor: colors.Primary400,
        borderBottomWidth: 1,
        height: 64,
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        overflow: 'hidden',
    },
    containerUnderBreakpoint: {
        marginHorizontal: 16,
    },
    itemsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
    },
    scrollArrow: {
        width: 28,
        height: 48,
        alignSelf: 'flex-end',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
    },
    scrollArrowDisabled: {
        opacity: 0.4,
    },
    containerMobile: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: colors.Primary400,
        paddingLeft: 18,
        overflow: 'hidden',
    },
    subContainerMobile: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.Primary400,
        height: 56,
    },
    subMobile: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    itemsContainerMobile: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: colors.Primary400,
        height: 0,
        marginLeft: -18,
    },
    karmaBarDetailsContainer: {
        flex: 1,
        flexDirection: 'row',
        alignSelf: 'flex-end',
        backgroundColor: colors.Primary400,
        height: 0,
    },
    karmaBarDetailsParent: {
        marginTop: 0,
        marginBottom: 0,
        justifyContent: 'center',
    },
    karmaBarDetails: {
        height: 36,
        backgroundColor: colors.Primary350,
    },
})
